import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth-context";
import { Sparkles, Loader2 } from "lucide-react";

export default function DemoAccess() {
  const { token } = useParams();
  const { setSession } = useAuth();
  const nav = useNavigate();
  const [error, setError] = useState(null);
  const [info, setInfo] = useState(null);
  const ranRef = useRef(false);

  useEffect(() => {
    if (ranRef.current) return;
    ranRef.current = true;
    (async () => {
      try {
        const r = await api.post("/auth/demo/redeem", { token });
        // tabOnly: true → write the demo session to sessionStorage (per-tab),
        // so an admin previewing the demo portal doesn't lose their admin
        // session in the original tab.
        setSession(r.data.token, r.data.user, { tabOnly: true });
        setInfo(r.data?.user || null);
        // Redirect after a beat so the user sees the welcome screen.
        setTimeout(() => nav("/portal", { replace: true }), 2200);
      } catch (e) {
        setError(formatApiError(e));
      }
    })();
  }, [token]);

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <div className="max-w-md text-center" data-testid="demo-access-error">
          <div className="font-display text-xl font-semibold text-slate-900 mb-2">Demo link unavailable</div>
          <p className="text-sm text-slate-500">{error}</p>
          <a href="/" className="text-xs text-blue-700 hover:text-blue-900 underline mt-3 inline-block">
            Return home
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center p-6">
      <div className="w-full max-w-md text-center" data-testid="demo-access-loading">
        <div className="w-14 h-14 rounded-2xl bg-blue-600 mx-auto flex items-center justify-center text-white mb-4">
          <Sparkles className="w-7 h-7" />
        </div>
        <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-blue-700">Nodera Health · Demo</div>
        {info ? (
          <>
            <h1 className="font-display text-2xl font-bold text-slate-900 mt-2">
              Welcome, {info.name?.split(" ")[0] || "demo"}.
            </h1>
            <p className="text-sm text-slate-600 mt-2 max-w-sm mx-auto">
              You're entering the demo experience for <span className="font-semibold">{info.organization || info.clinic_name}</span>.
              Submissions and invoices stay fully sandboxed — nothing leaves this environment.
            </p>
            <ul className="mt-4 text-left text-xs text-slate-600 space-y-1.5 max-w-sm mx-auto">
              <li>⏱ Workflows auto-advance every 60 seconds.</li>
              <li>⚡ Click "Simulate vendor response" to skip ahead.</li>
              <li>🔒 No real emails, SMS, or payments are triggered.</li>
            </ul>
            <div className="mt-4 inline-flex items-center gap-2 text-xs text-slate-500">
              <Loader2 className="w-3 h-3 animate-spin" /> Setting up your portal…
            </div>
          </>
        ) : (
          <>
            <h1 className="font-display text-2xl font-bold text-slate-900 mt-2">Authenticating your demo link…</h1>
            <div className="mt-4 inline-flex items-center gap-2 text-xs text-slate-500">
              <Loader2 className="w-3 h-3 animate-spin" /> One moment
            </div>
          </>
        )}
      </div>
    </div>
  );
}
